import { useState, useEffect, useCallback, useMemo } from 'react';
import { BIBLE_BOOKS, getChaptersForBook, getVersesForChapter, getBookInfo } from '../utils/bibleData';

// Fast (synchronous) cache of the last record point so the Record page opens
// directly there instead of flashing Genesis 1:1 before the async advancement runs.
const RECORD_POINT_KEY = 'fb_recordPoint';
const readRecordPoint = () => {
  try {
    return JSON.parse(localStorage.getItem(RECORD_POINT_KEY) || 'null');
  } catch {
    return null;
  }
};

/**
 * Custom hook for managing verse selection and plan advancement
 * Handles book/chapter/verse selection, auto-advancement, and plan management
 */
export const useVerseSelection = ({
  currentUser,
  existingRecordings = [],
  onVerseRangeChange
}) => {
  // Core selection state — restore from the fast localStorage cache on mount.
  const [selectedBook, setSelectedBook] = useState(() => readRecordPoint()?.book || 'genesis');
  const [selectedChapter, setSelectedChapter] = useState(() => readRecordPoint()?.chapter || 1);
  const [startVerse, setStartVerse] = useState(() => readRecordPoint()?.startVerse || 1);
  const [endVerse, setEndVerse] = useState(() => readRecordPoint()?.endVerse || 1);

  // Persist the current record point so the next visit opens here instantly.
  useEffect(() => {
    try {
      localStorage.setItem(
        RECORD_POINT_KEY,
        JSON.stringify({ book: selectedBook, chapter: selectedChapter, startVerse, endVerse })
      );
    } catch {}
  }, [selectedBook, selectedChapter, startVerse, endVerse]);
  
  // Available options
  const [availableChapters, setAvailableChapters] = useState([]);
  const [availableVerses, setAvailableVerses] = useState([]);
  const [availableEndVerses, setAvailableEndVerses] = useState([]);
  

  // Update available chapters when book changes
  useEffect(() => {
    const chapters = getChaptersForBook(selectedBook);
    setAvailableChapters(chapters);
    if (!chapters.includes(selectedChapter)) {
      setSelectedChapter(1);
    }
  }, [selectedBook, selectedChapter]);

  // Update available verses when chapter changes
  useEffect(() => {
    const verses = getVersesForChapter(selectedBook, selectedChapter);
    setAvailableVerses(verses);
    setAvailableEndVerses(verses);
    
    // Reset verse selection if current selection is invalid
    if (startVerse && (startVerse < 1 || startVerse > verses.length)) {
      setStartVerse(1);
    }
    if (endVerse && (endVerse < 1 || endVerse > verses.length)) {
      setEndVerse(verses.length);
    }
    
    // Ensure end verse is not before start verse
    if (startVerse && endVerse && endVerse < startVerse) {
      setEndVerse(startVerse);
    }
  }, [selectedBook, selectedChapter, startVerse, endVerse]);

  // Update available end verses when start verse changes
  useEffect(() => {
    const verses = getVersesForChapter(selectedBook, selectedChapter);
    // Filter to only show verses from start verse onwards
    const endVerseOptions = verses.filter(verse => verse >= startVerse);
    setAvailableEndVerses(endVerseOptions);
    
    // If current end verse is less than start verse, set it to start verse
    if (endVerse < startVerse) {
      setEndVerse(startVerse);
    }
  }, [selectedBook, selectedChapter, startVerse, endVerse]);



  // Computed values
  const totalVerses = useMemo(() => endVerse - startVerse + 1, [endVerse, startVerse]);
  const passageString = useMemo(() => {
    if (startVerse && endVerse && startVerse !== endVerse) {
      return `${getBookInfo(selectedBook)?.name} ${selectedChapter}:${startVerse}-${endVerse}`;
    } else if (startVerse) {
      return `${getBookInfo(selectedBook)?.name} ${selectedChapter}:${startVerse}`;
    } else {
      return `${getBookInfo(selectedBook)?.name} ${selectedChapter}`;
    }
  }, [selectedBook, selectedChapter, startVerse, endVerse]);

  return {
    // Selection state
    selectedBook,
    selectedChapter,
    startVerse,
    endVerse,
    
    // Available options
    availableChapters,
    availableVerses,
    availableEndVerses,
    
    // State setters
    setSelectedBook,
    setSelectedChapter,
    setStartVerse,
    setEndVerse,
    
    // Computed values
    totalVerses,
    passageString
  };
};

export default useVerseSelection;
